import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class InactivityService {
  private inactivityTimeout = 10 * 60 * 1000; // 10 minutes in milliseconds
  private checkInterval = 30 * 1000; // how often the idle time is evaluated
  private activityWriteThrottle = 1000; // avoid writing localStorage on every mousemove
  private lastActivityWrite = 0;
  private signingOut = false;
  private inactivitySubject = new Subject<void>();

  constructor() {
    this.touch();
    this.setupListeners();
    // A single setTimeout is not reliable: it is paused while the machine sleeps and never re-arms
    // when another tab kept the shared timestamp fresh. Poll the shared timestamp instead.
    setInterval(() => this.checkInactivity(), this.checkInterval);
  }

  private setupListeners(): void {
    ['mousemove', 'keydown', 'mousedown', 'wheel', 'touchstart'].forEach(eventName =>
      window.addEventListener(eventName, () => this.resetTimer(), { passive: true }));
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.checkInactivity();
      }
    });
  }

  private getLastActivity(): number {
    return parseInt(localStorage.getItem('lastActivityTimestamp'), 10) || 0;
  }

  private isExpired(): boolean {
    const lastActivity = this.getLastActivity();
    return lastActivity > 0 && Date.now() - lastActivity > this.inactivityTimeout;
  }

  private touch(): void {
    this.lastActivityWrite = Date.now();
    localStorage.setItem('lastActivityTimestamp', this.lastActivityWrite.toString());
  }

  private checkInactivity(): void {
    if (!this.signingOut && this.isExpired()) {
      this.inactivitySubject.next();
    }
  }

  resetTimer(): void {
    // The first input after a long idle period (e.g. waking the PC) must not revive an expired session.
    if (this.isExpired()) {
      this.checkInactivity();
      return;
    }
    if (Date.now() - this.lastActivityWrite >= this.activityWriteThrottle) {
      this.touch();
    }
  }

  signOut(): void {
    if (this.signingOut) {
      return;
    }
    this.signingOut = true;
    // Fresh timestamp so the next login does not start out as expired.
    this.touch();
    // Other open tabs listen for this flag and log out as well.
    localStorage.setItem('isLoggedin', 'false');
    window.location.href = '/authorization/signout';
  }

  inactivityObservable(): Observable<void> {
    return this.inactivitySubject.asObservable();
  }
}
